import { useCallback, useEffect, useRef, useState } from 'react';

import { listarTurmas } from '../../jogadores/api/jogadores';
import type { TurmaResumo } from '../../jogadores/api/jogadores';
import { listarOcorrencias } from '../api/ocorrencias';
import type { Criticidade, Ocorrencia, OcorrenciasDaTurma, TipoDeOcorrencia } from '../api/ocorrencias';
import { manterSeDisponivel, useTurmaEmUso } from '../../../shared/turmaEmUso';

/** Como a Operação: relê a cada 15 s, só com a aba do navegador visível. */
const INTERVALO_MS = 15_000;

const CRITICIDADE: Record<Criticidade, string> = { critica: 'Crítica', atencao: 'Atenção' };

const TIPO: Record<TipoDeOcorrencia, string> = {
    login_recusado: 'Login recusado',
    sessao_revogada: 'Sessão encerrada',
    apuracao_pendente: 'Apuração pendente',
    tablet_offline: 'Tablet offline',
};

const FILTROS_VAZIOS = { grupo: '', criticidade: '', tipo: '' };

/** "Grupo 03" de "Grupo 03", "Grupo G01" de um grupo sem nome. */
function rotuloDoGrupo(ocorrencia: Ocorrencia) {
    if (!ocorrencia.grupo_id) return 'Sem grupo';
    const nome = ocorrencia.grupo_nome?.trim();
    return nome && /^grupo\b/i.test(nome) ? nome : `Grupo ${nome || ocorrencia.grupo_id}`;
}

const hora = (instante: string) =>
    new Date(instante).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const semAcento = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function CartaoDeOcorrencia({ ocorrencia }: { ocorrencia: Ocorrencia }) {
    const onde = [rotuloDoGrupo(ocorrencia), ocorrencia.fase_rotulo].filter(Boolean).join(' · ');
    const quem = [ocorrencia.nome, ocorrencia.matricula].filter(Boolean).join(' · ');
    return (
        <li className="cartao-ocorrencia">
            <div className="topo-ocorrencia">
                <span className={`selo-criticidade selo-${ocorrencia.criticidade}`}>{CRITICIDADE[ocorrencia.criticidade]}</span>
                <span className="onde-ocorrencia">{onde}</span>
                <time className="hora-ocorrencia" dateTime={ocorrencia.em}>{hora(ocorrencia.em)}</time>
            </div>
            <p className="titulo-ocorrencia">{ocorrencia.titulo}</p>
            {(quem || ocorrencia.tablet) && (
                <p className="detalhe-ocorrencia">
                    {quem && <span>Participante: <strong>{quem.toUpperCase()}</strong></span>}
                    {ocorrencia.tablet && <span>Tablet: <strong>{ocorrencia.tablet}</strong></span>}
                </p>
            )}
        </li>
    );
}

/**
 * Aba Ocorrências (`RA18`, modelo `docs/img_refac/ocorrencia.png`): os tipos da `DP-RA-021` — login
 * recusado, sessão encerrada pelo painel, resultado sem apuração e tablet offline com o grupo
 * jogando —, já montados pelo servidor. Busca e filtros são da tela, sobre a lista da turma.
 */
export function OcorrenciasPainel() {
    const [turmas, setTurmas] = useState<TurmaResumo[]>([]);
    const [turmaId, setTurmaId] = useTurmaEmUso();
    const [dados, setDados] = useState<OcorrenciasDaTurma | null>(null);
    const [erro, setErro] = useState(false);
    const [busca, setBusca] = useState('');
    const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
    // Uma resposta atrasada da turma anterior não pode cobrir a da turma escolhida.
    const turmaAtual = useRef<string | null>(null);

    const carregar = useCallback(async (alvo: string) => {
        try {
            const resposta = await listarOcorrencias(alvo);
            if (turmaAtual.current !== alvo) return;
            setDados(resposta);
            setErro(false);
        } catch {
            if (turmaAtual.current === alvo) setErro(true);
        }
    }, []);

    useEffect(() => {
        listarTurmas()
            .then((lista) => {
                const ativas = lista.filter((turma) => turma.ativa);
                setTurmas(ativas);
                setTurmaId((atual) => manterSeDisponivel(atual, ativas.map((turma) => turma.id), ativas[0]?.id ?? null));
            })
            .catch(() => setErro(true));
    }, []);

    useEffect(() => {
        turmaAtual.current = turmaId;
        setFiltros(FILTROS_VAZIOS);
        if (!turmaId) return undefined;
        void carregar(turmaId);
        const relogio = window.setInterval(() => {
            if (document.visibilityState === 'visible') void carregar(turmaId);
        }, INTERVALO_MS);
        return () => window.clearInterval(relogio);
    }, [turmaId, carregar]);

    const termo = semAcento(busca.trim());
    const visiveis = (dados?.ocorrencias ?? []).filter((ocorrencia) => {
        if (filtros.grupo && ocorrencia.grupo_id !== filtros.grupo) return false;
        if (filtros.criticidade && ocorrencia.criticidade !== filtros.criticidade) return false;
        if (filtros.tipo && ocorrencia.tipo !== filtros.tipo) return false;
        if (!termo) return true;
        const texto = [ocorrencia.titulo, ocorrencia.nome, ocorrencia.matricula, ocorrencia.tablet, rotuloDoGrupo(ocorrencia)]
            .filter(Boolean)
            .join(' ');
        return semAcento(texto).includes(termo);
    });
    const filtrando = Boolean(termo || filtros.grupo || filtros.criticidade || filtros.tipo);

    return (
        <div className="ocorrencias">
            <div className="barra-turma-operacao">
                <label htmlFor="turma-ocorrencias">ID da Turma<span aria-hidden="true">:</span></label>
                <select id="turma-ocorrencias" value={turmaId ?? ''} onChange={(evento) => setTurmaId(evento.target.value)}>
                    {turmas.map((turma) => (
                        <option key={turma.id} value={turma.id}>{turma.id}</option>
                    ))}
                </select>
            </div>

            <div className="barra-participantes">
                <input
                    type="search"
                    className="busca-participantes"
                    aria-label="Buscar ocorrência"
                    placeholder="Buscar por ocorrência, participante ou grupo"
                    value={busca}
                    onChange={(evento) => setBusca(evento.target.value)}
                />
                <div className="filtros-participantes">
                    <select aria-label="Grupo" value={filtros.grupo} onChange={(evento) => setFiltros({ ...filtros, grupo: evento.target.value })}>
                        <option value="">Grupo</option>
                        {(dados?.grupos ?? []).map((grupo) => (
                            <option key={grupo.id} value={grupo.id}>{grupo.nome ?? grupo.id}</option>
                        ))}
                    </select>
                    <select aria-label="Criticidade" value={filtros.criticidade} onChange={(evento) => setFiltros({ ...filtros, criticidade: evento.target.value })}>
                        <option value="">Criticidade</option>
                        {Object.entries(CRITICIDADE).map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
                    </select>
                    <select aria-label="Tipo" value={filtros.tipo} onChange={(evento) => setFiltros({ ...filtros, tipo: evento.target.value })}>
                        <option value="">Tipo</option>
                        {Object.entries(TIPO).map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
                    </select>
                    <button
                        className="botao-texto"
                        type="button"
                        onClick={() => {
                            setBusca('');
                            setFiltros(FILTROS_VAZIOS);
                        }}
                    >
                        Limpar filtros
                    </button>
                </div>
            </div>

            {erro && <p className="erro-operacao" role="alert">Não foi possível carregar as ocorrências.</p>}
            {dados && visiveis.length === 0 && (
                <p className="vazio-ocorrencias">
                    {filtrando ? 'Nenhuma ocorrência encontrada.' : 'Nenhuma ocorrência nas últimas 24 horas.'}
                </p>
            )}
            {visiveis.length > 0 && (
                <ul className="lista-ocorrencias" aria-label="Ocorrências">
                    {visiveis.map((ocorrencia) => <CartaoDeOcorrencia key={ocorrencia.id} ocorrencia={ocorrencia} />)}
                </ul>
            )}
        </div>
    );
}
